import { normalizePhone } from "@elektr/shared";
import { useQuery } from "@tanstack/react-query";
import { Zap } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";

import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Field, Input } from "@/components/ui/form";
import { ApiError, api, errorMessage, unwrap } from "@/lib/api";
import { useT } from "@/lib/i18n";

type Step = "request" | "reset" | "done";

/** Parolni tiklash: telefon yoki email -> kod -> yangi parol. Foydalanuvchi bor-yo'qligi oshkor qilinmaydi. */
export function ForgotPasswordPage() {
  const { t } = useT();
  const cfg = useQuery({
    queryKey: ["public-config"],
    queryFn: async () => unwrap(await api.GET("/api/public-config/")),
    staleTime: 60_000,
    retry: false,
  });
  const [step, setStep] = useState<Step>("request");
  const [identifier, setIdentifier] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const ident = identifier.trim();
  const valid = ident.includes("@") ? /^\S+@\S+\.\S+$/.test(ident) : !!normalizePhone(ident);
  const value = ident.includes("@") ? ident.toLowerCase() : (normalizePhone(ident) ?? ident);

  async function run(fn: () => Promise<void>) {
    setError(null);
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      setError(errorMessage(e, t("common.error")));
    } finally {
      setBusy(false);
    }
  }

  const request = (e: FormEvent) => {
    e.preventDefault();
    if (!valid) return setError(t("auth.identifier_invalid"));
    void run(async () => {
      unwrap(await api.POST("/api/auth/password/forgot/", { body: { identifier: value } }));
      setStep("reset");
    });
  };

  const reset = (e: FormEvent) => {
    e.preventDefault();
    if (password.length < 8) return setError(t("users.password_short"));
    if (password !== repeat) return setError(t("auth.password_mismatch"));
    void run(async () => {
      const res = await api.POST("/api/auth/password/reset/", {
        body: { identifier: value, code: code.trim(), new_password: password },
      });
      if (res.response.status === 400) {
        const body = res.error as { code?: string; new_password?: string[] } | undefined;
        setError(
          body?.code === "invalid_code"
            ? t("auth.reset_code_invalid")
            : (body?.new_password?.join(" ") ?? t("common.error")),
        );
        return;
      }
      if (!res.response.ok) throw new ApiError(res.response.status, res.error);
      setStep("done");
    });
  };

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-sm p-6">
        <div className="mb-5 flex items-center justify-between">
          <div className="flex items-center gap-2 text-xl font-bold">
            <Zap className="size-6 text-primary" /> Elektr Pasport
          </div>
          <ThemeToggle />
        </div>
        <h1 className="mb-3 text-lg font-bold">{t("auth.reset_title")}</h1>

        {cfg.data && !cfg.data.reset_enabled && (
          <p role="alert" className="mb-3 rounded-lg bg-muted p-3 text-sm">
            {t("auth.reset_unavailable")}
          </p>
        )}

        {step === "request" && (
          <form onSubmit={request} className="flex flex-col gap-4" noValidate>
            <p className="text-sm text-muted-foreground">{t("auth.reset_hint")}</p>
            <Field label={t("auth.identifier")}>
              {(id) => (
                <Input
                  id={id}
                  autoComplete="username"
                  inputMode="email"
                  placeholder="90 123 45 67 / ism@mail.uz"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                />
              )}
            </Field>
            {error && (
              <p role="alert" className="text-sm text-danger">
                {error}
              </p>
            )}
            <Button type="submit" disabled={busy}>
              {t("auth.send_code")}
            </Button>
          </form>
        )}

        {step === "reset" && (
          <form onSubmit={reset} className="flex flex-col gap-4" noValidate>
            <p className="text-sm text-muted-foreground">{t("auth.reset_sent")}</p>
            <Field label={t("auth.code")}>
              {(id) => (
                <Input
                  id={id}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                />
              )}
            </Field>
            <Field label={t("auth.new_password")} hint={t("users.password_hint_short")}>
              {(id) => (
                <Input
                  id={id}
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              )}
            </Field>
            <Field label={t("auth.repeat_password")}>
              {(id) => (
                <Input
                  id={id}
                  type="password"
                  autoComplete="new-password"
                  value={repeat}
                  onChange={(e) => setRepeat(e.target.value)}
                />
              )}
            </Field>
            {error && (
              <p role="alert" className="text-sm text-danger">
                {error}
              </p>
            )}
            <Button type="submit" disabled={busy || code.length < 4}>
              {t("auth.reset_submit")}
            </Button>
            <Button type="button" variant="ghost" onClick={() => setStep("request")}>
              {t("auth.reset_resend")}
            </Button>
          </form>
        )}

        {step === "done" && (
          <div className="flex flex-col gap-4">
            <p role="status" className="text-sm text-success">
              {t("auth.reset_done")}
            </p>
            <Link
              to="/login"
              className="inline-flex min-h-11 items-center justify-center rounded-lg bg-primary px-4 font-semibold text-primary-foreground"
            >
              {t("auth.login")}
            </Link>
          </div>
        )}
        {step !== "done" && (
          <Link
            to="/login"
            className="mt-4 block text-center text-sm font-medium text-primary underline-offset-4 hover:underline"
          >
            {t("auth.back_to_login")}
          </Link>
        )}
      </Card>
    </div>
  );
}
